import { skipHydrate } from 'pinia'
import type { AuthCredentials } from '@base/types'
import type { RouteLocationNormalized } from 'vue-router'

export const useAuth = defineStore('auth', () => {
  const client = useSupabaseClient()
  const authUser = useSupabaseUser()

  const registerWithPassword = async (credentials: AuthCredentials, metadata?: Record<string, any>) => {
    if (!credentials.email || !credentials.password)
      return

    try {
      await $fetch('/api/auth', {
        method: 'POST',
        body: {
          email: credentials.email,
          password: credentials.password,
          redirectTo: `${useRequestURL().origin}/email-verified`,
          metadata,
        },
        headers: useRequestHeaders(['cookie']),
      })
    }
    catch (e: any) {
      console.error(e)
      return createError(e.message)
    }
  }

  const registerWithPasswordMoneioCash = async (credentials: AuthCredentials, metadata?: Record<string, any>) => {
    if (!credentials.email || !credentials.password)
      return

    try {
      await $fetch('/api/exchange-offices', {
        method: 'POST',
        body: {
          email: credentials.email,
          password: credentials.password,
          redirectTo: `${useRequestURL().origin}/email-verified`,
          metadata,
        },
        headers: useRequestHeaders(['cookie']),
      })
    }
    catch (e: any) {
      console.error(e)
      return createError(e.message)
    }
  }

  const resolveSession = async (url: RouteLocationNormalized) => {
    // check if the url contains access and refresh token
    const urlHash = new URLSearchParams(url.hash.replace('#', ''))
    const access_token = urlHash.get('access_token')
    const refresh_token = urlHash.get('refresh_token')
    if (access_token && refresh_token) {
      // set the tokens to the supabase client
      await client.auth.setSession({
        access_token,
        refresh_token,
      })
    }
  }

  const loginWithPassword = async (credentials: AuthCredentials) => {
    if (!credentials.email || !credentials.password)
      return
    const { error } = await client.auth.signInWithPassword(credentials)
    if (error)
      throw new Error(error.message)

    if (!authUser.value)
      return createError('Auth Failed')
  }

  const loginWithPasswordMoneioCash = async (credentials: AuthCredentials) => {
    if (!credentials.email || !credentials.password)
      return

    try {
      const { exists } = await $fetch<{ exists: boolean }>(`/api/users/${encodeURIComponent(credentials.email)}/exchange-office/exists`)

      if (!exists) {
        throw createError({
          statusCode: 404,
          statusMessage: 'Missing Exchange office ID',
        })
      }
    }
    catch (e: any) {
      throw createError({
        statusCode: e.statusCode || 500,
        statusMessage: e.message || 'Failed to verify exchange office',
      })
    }

    const { error } = await client.auth.signInWithPassword(credentials)
    if (error)
      throw new Error(error.message)

    if (!authUser.value)
      return createError('Auth Failed')
  }

  const loginWithProvider = async (provider: 'discord' | 'google' | 'facebook') => {
    let options = {
      redirectTo: `${useRequestURL().origin}`,
      scope: '',
    }
    if (provider === 'discord') {
      options = {
        ...options,
        scope: 'connections',
      }
    }

    const { error } = await client.auth.signInWithOAuth({ provider, options })
    if (error)
      return error

    if (!authUser.value)
      return createError('Auth Failed')
  }

  const sendResetPasswordEmail = async (email: string) => {
    try {
      await $fetch(`/api/auth/${encodeURIComponent(email)}/password/reset`, {
        method: 'POST',
        body: {
          redirectTo: `${useRequestURL().origin}/update-password`,
        },
        headers: useRequestHeaders(['cookie']),
      })
    }
    catch (e: any) {
      console.error(e)
      return createError(e)
    }
  }

  // to be used with forgotten password emails. Not needed to enter old password
  const changeForgottenPassword = async (newPassword: string) => {
    const { error } = await client.auth.updateUser({ password: newPassword })
    if (error)
      return createError(error.message)
  }

  // to be used to change password when user knows old password
  const setNewPassword = async (credentials: AuthCredentials, newPassword: string) => {
    const { $t } = useI18n()

    try {
      const errorLogin = await loginWithPassword(credentials)
      if (errorLogin)
        return createError(String($t('components.validations.invalidOldPassword')))
    }
    catch (e) {
      console.error(e)
      return createError(String($t('components.validations.invalidOldPassword')))
    }

    try {
      await $fetch(`/api/auth/password`, {
        method: 'PATCH',
        body: {
          newPassword,
        },
        headers: useRequestHeaders(['cookie']),
      })
    }
    catch (e: any) {
      console.error(e)
      return createError(e)
    }
  }

  const deleteAuthUser = async (email: string, isAdmin: boolean) => {
    const encodedEmail = encodeURIComponent(email)

    const url = isAdmin ? `/api/admin/auth/${encodedEmail}` : `/api/auth/${encodedEmail}`

    return await $superFetch<{ success: boolean }>(url, {
      method: 'DELETE',
    })
  }

  const logout = async () => {
    const { $switchRoute } = useI18n()

    const { error } = await client.auth.signOut()
    if (error)
      throw createError(error.message)
    $switchRoute('/')
  }

  const getSession = async () => {
    return (await client.auth.getSession()).data.session
  }

  const markUserAsAdmin = async () => {
    const metadata = authUser.value?.user_metadata ?? {}
    if (metadata.isAdmin)
      return
    metadata.isAdmin = true
    const { error } = await client.auth.updateUser({ data: metadata })
    if (error)
      return createError(error.message)
  }

  return {
    authUser,
    loginWithPassword,
    loginWithPasswordMoneioCash,
    loginWithProvider,
    registerWithPassword,
    registerWithPasswordMoneioCash,
    sendResetPasswordEmail,
    changeForgottenPassword,
    setNewPassword,
    deleteAuthUser,
    logout,
    getSession,
    markUserAsAdmin,
    resolveSession: skipHydrate(resolveSession),
  }
})
